import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiBriefcase,
    FiCheck,
    FiCheckCircle,
    FiCreditCard,
    FiDollarSign,
    FiHash,
    FiInfo,
    FiLoader,
    FiMessageSquare,
    FiSmartphone,
    FiX,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";
import { config } from "../../../../../../utils/apiUrl";
import useSwal from "../../../../../../hooks/useSwal";

import "./SaleInvoicePaymentDialog.css";

/*
|--------------------------------------------------------------------------
| CONSTANTS
|--------------------------------------------------------------------------
*/

const PAYMENT_METHODS = [
    {
        value: "CASH",
        label: "Cash",
        icon: FiDollarSign,
        referenceRequired: false,
        referenceLabel: "Receipt / Reference Number",
        referencePlaceholder: "Optional receipt reference",
    },
    {
        value: "UPI",
        label: "UPI",
        icon: FiSmartphone,
        referenceRequired: false,
        referenceLabel: "UPI Transaction ID",
        referencePlaceholder: "Optional UPI transaction ID",
    },
    {
        value: "CARD",
        label: "Card",
        icon: FiCreditCard,
        referenceRequired: false,
        referenceLabel: "Card Transaction Reference",
        referencePlaceholder: "Optional card transaction reference",
    },
    {
        value: "BANK_TRANSFER",
        label: "Bank Transfer",
        icon: FiBriefcase,
        referenceRequired: false,
        referenceLabel: "Bank Reference / UTR",
        referencePlaceholder: "Optional UTR or bank reference",
    },
    {
        value: "CHEQUE",
        label: "Cheque",
        icon: FiHash,
        referenceRequired: false,
        referenceLabel: "Cheque Number",
        referencePlaceholder: "Optional cheque number",
    },
    {
        value: "WALLET",
        label: "Wallet",
        icon: FiSmartphone,
        referenceRequired: false,
        referenceLabel: "Wallet Transaction ID",
        referencePlaceholder: "Optional wallet transaction ID",
    },
    {
        value: "OTHER",
        label: "Other",
        icon: FiCreditCard,
        referenceRequired: false,
        referenceLabel: "Reference Number",
        referencePlaceholder: "Optional payment reference",
    },
];

const EMPTY_FORM = {
    amount: "",
    paymentDate: "",
    paymentMethod: "CASH",
    referenceNumber: "",
    transactionId: "",
    notes: "",
};

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const parsed =
        Number(value);

    return Number.isFinite(
        parsed
    )
        ? parsed
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(value) +
            Number.EPSILON
        ) *
            100
    ) / 100;

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    ).format(
        roundMoney(value)
    );

const getTodayDate = () => {
    const now =
        new Date();

    const offset =
        now.getTimezoneOffset() *
        60 *
        1000;

    return new Date(
        now.getTime() -
        offset
    )
        .toISOString()
        .slice(0, 10);
};

const getInvoiceId = (
    invoice
) =>
    invoice?._id ||
    invoice?.id ||
    "";

const getInvoiceNumber = (
    invoice
) =>
    invoice?.documentNumber ||
    invoice?.invoiceNumber ||
    invoice?.invoiceNo ||
    invoice?.number ||
    "-";

const getCustomerName = (
    invoice
) =>
    invoice?.customerSnapshot
        ?.customerName ||
    invoice?.customerSnapshot
        ?.name ||
    invoice?.customer
        ?.customerName ||
    invoice?.customer?.name ||
    invoice?.customerName ||
    "Walk-in Customer";

const getGrandTotal = (
    invoice
) =>
    toNumber(
        invoice?.totals
            ?.grandTotal ??
        invoice?.totals
            ?.totalAmount ??
        invoice?.grandTotal ??
        invoice?.totalAmount ??
        invoice?.netAmount
    );

const getPaidAmount = (
    invoice
) =>
    toNumber(
        invoice?.totals
            ?.paidAmount ??
        invoice?.paidAmount ??
        invoice?.paymentSummary
            ?.paidAmount
    );

const getBalanceAmount = (
    invoice
) => {
    const savedBalance =
        invoice?.totals
            ?.balanceAmount ??
        invoice?.balanceAmount ??
        invoice?.paymentSummary
            ?.balanceAmount;

    if (
        savedBalance !==
            undefined &&
        savedBalance !==
            null
    ) {
        return Math.max(
            roundMoney(
                savedBalance
            ),
            0
        );
    }

    return Math.max(
        roundMoney(
            getGrandTotal(
                invoice
            ) -
                getPaidAmount(
                    invoice
                )
        ),
        0
    );
};

const getResponseError = (
    error,
    fallback
) =>
    error?.response?.data
        ?.message ||
    error?.response?.data
        ?.error?.message ||
    error?.message ||
    fallback;

const isSuccessfulResponse = (
    response
) => {
    if (
        response?.status ===
            "success" ||
        response?.success ===
            true
    ) {
        return true;
    }

    if (
        response?.data &&
        (
            response.data
                ?.payment ||
            response.data
                ?.invoice ||
            response.data
                ?.allocation
        )
    ) {
        return true;
    }

    return false;
};

const validatePayment = ({
    form,
    balanceAmount,
    paymentMethod,
}) => {
    const errors = {};

    const amount =
        toNumber(
            form.amount,
            NaN
        );

    if (
        !Number.isFinite(
            amount
        ) ||
        amount <= 0
    ) {
        errors.amount =
            "Enter a valid payment amount.";
    } else if (
        roundMoney(amount) >
        roundMoney(
            balanceAmount
        )
    ) {
        errors.amount =
            `Payment cannot exceed ${formatCurrency(
                balanceAmount
            )}.`;
    }

    if (
        !form.paymentDate
    ) {
        errors.paymentDate =
            "Payment date is required.";
    }

    if (
        !form.paymentMethod
    ) {
        errors.paymentMethod =
            "Payment method is required.";
    }

    if (
        String(
            form.notes ||
            ""
        ).length >
        500
    ) {
        errors.notes =
            "Notes cannot exceed 500 characters.";
    }

    return errors;
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const SaleInvoicePaymentDialog = ({
    open,
    invoice,
    saleInvoice,
    paymentMethods =
        PAYMENT_METHODS,
    onClose,
    onSuccess,
}) => {
    const resolvedInvoice =
        invoice ||
        saleInvoice;

    const api =
        useMemo(
            () =>
                new API(),
            []
        );

    const swal =
        useSwal();

    const amountInputRef =
        useRef(null);

    const [
        form,
        setForm,
    ] = useState({
        ...EMPTY_FORM,
        paymentDate:
            getTodayDate(),
    });

    const [
        errors,
        setErrors,
    ] = useState({});

    const [
        submitting,
        setSubmitting,
    ] = useState(false);

    const [
        submitError,
        setSubmitError,
    ] = useState("");

    const invoiceId =
        getInvoiceId(
            resolvedInvoice
        );

    const invoiceNumber =
        getInvoiceNumber(
            resolvedInvoice
        );

    const customerName =
        getCustomerName(
            resolvedInvoice
        );

    const grandTotal =
        getGrandTotal(
            resolvedInvoice
        );

    const alreadyPaid =
        getPaidAmount(
            resolvedInvoice
        );

    const balanceAmount =
        getBalanceAmount(
            resolvedInvoice
        );

    const enteredAmount =
        Math.max(
            roundMoney(
                form.amount
            ),
            0
        );

    const remainingBalance =
        Math.max(
            roundMoney(
                balanceAmount -
                enteredAmount
            ),
            0
        );

    const isFullPayment =
        enteredAmount >
            0 &&
        roundMoney(
            enteredAmount
        ) ===
            roundMoney(
                balanceAmount
            );

    const selectedMethod =
        useMemo(
            () =>
                paymentMethods.find(
                    (
                        method
                    ) =>
                        method.value ===
                        form.paymentMethod
                ) ||
                PAYMENT_METHODS[0],
            [
                form.paymentMethod,
                paymentMethods,
            ]
        );

    useEffect(() => {
        if (
            !open ||
            !resolvedInvoice
        ) {
            return;
        }

        const balance =
            getBalanceAmount(
                resolvedInvoice
            );

        setForm({
            ...EMPTY_FORM,

            amount:
                balance > 0
                    ? String(
                        roundMoney(
                            balance
                        )
                    )
                    : "",

            paymentDate:
                getTodayDate(),

            paymentMethod:
                "CASH",
        });

        setErrors({});
        setSubmitError("");
        setSubmitting(false);

        window.setTimeout(
            () => {
                amountInputRef
                    .current
                    ?.focus();

                amountInputRef
                    .current
                    ?.select();
            },
            120
        );
    }, [
        open,
        resolvedInvoice,
    ]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const originalOverflow =
            document.body.style
                .overflow;

        document.body.style.overflow =
            "hidden";

        const handleEscape = (
            event
        ) => {
            if (
                event.key ===
                    "Escape" &&
                !submitting
            ) {
                onClose?.();
            }
        };

        document.addEventListener(
            "keydown",
            handleEscape
        );

        return () => {
            document.body.style.overflow =
                originalOverflow;

            document.removeEventListener(
                "keydown",
                handleEscape
            );
        };
    }, [
        onClose,
        open,
        submitting,
    ]);

    const handleChange =
        useCallback(
            (
                event
            ) => {
                const {
                    name,
                    value,
                } =
                    event.target;

                setForm(
                    (
                        current
                    ) => ({
                        ...current,
                        [name]:
                            value,
                    })
                );

                setErrors(
                    (
                        current
                    ) => ({
                        ...current,
                        [name]:
                            "",
                    })
                );

                setSubmitError(
                    ""
                );
            },
            []
        );

    const setAmount =
        useCallback(
            (
                amount
            ) => {
                setForm(
                    (
                        current
                    ) => ({
                        ...current,

                        amount:
                            String(
                                roundMoney(
                                    amount
                                )
                            ),
                    })
                );

                setErrors(
                    (
                        current
                    ) => ({
                        ...current,
                        amount:
                            "",
                    })
                );
            },
            []
        );

    const handleMethodChange =
        useCallback(
            (
                methodValue
            ) => {
                setForm(
                    (
                        current
                    ) => ({
                        ...current,

                        paymentMethod:
                            methodValue,

                        referenceNumber:
                            "",

                        transactionId:
                            "",
                    })
                );

                setErrors(
                    (
                        current
                    ) => ({
                        ...current,

                        paymentMethod:
                            "",

                        referenceNumber:
                            "",
                    })
                );
            },
            []
        );

    const handleClose =
        useCallback(() => {
            if (
                submitting
            ) {
                return;
            }

            onClose?.();
        }, [
        onClose,
        submitting,
    ]);

    const handleBackdropClick =
        useCallback(
            (
                event
            ) => {
                if (
                    event.target ===
                    event.currentTarget
                ) {
                    handleClose();
                }
            },
            [handleClose]
        );

    const handleSubmit =
        useCallback(
            async (
                event
            ) => {
                event.preventDefault();

                const nextErrors =
                    validatePayment({
                        form,
                        balanceAmount,
                        paymentMethod:
                            selectedMethod,
                    });

                setErrors(
                    nextErrors
                );

                if (
                    Object.keys(
                        nextErrors
                    ).length
                ) {
                    return;
                }

                if (
                    !invoiceId
                ) {
                    setSubmitError(
                        "Sale invoice ID is missing."
                    );

                    return;
                }

                if (
                    !config
                        .createBillingPayment
                ) {
                    setSubmitError(
                        "config.createBillingPayment is not configured."
                    );

                    return;
                }

                const referenceNumber =
                    String(
                        form.referenceNumber ||
                        form.transactionId ||
                        ""
                    ).trim();

                const customerId =
                    resolvedInvoice
                        ?.customer
                        ?._id ||
                    resolvedInvoice
                        ?.customer ||
                    null;

                const payload = {
                    paymentDate:
                        form.paymentDate,

                    paymentMethod:
                        form.paymentMethod,

                    amount:
                        roundMoney(
                            form.amount
                        ),

                    paymentType:
                        "PAYMENT_IN",

                    direction:
                        "IN",

                    sourceType:
                        "SALE_INVOICE",

                    customer:
                        customerId,

                    customerData:
                        resolvedInvoice
                            ?.customerSnapshot ||
                        {},

                    branchId:
                        resolvedInvoice
                            ?.branchId ||
                        resolvedInvoice
                            ?.branch
                            ?._id ||
                        null,

                    firmId:
                        resolvedInvoice
                            ?.firmId ||
                        resolvedInvoice
                            ?.firm
                            ?._id ||
                        null,

                    referenceNumber,

                    transactionId:
                        referenceNumber,

                    bankReferenceNumber:
                        form.paymentMethod ===
                        "BANK_TRANSFER"
                            ? referenceNumber
                            : "",

                    upiTransactionId:
                        form.paymentMethod ===
                        "UPI"
                            ? referenceNumber
                            : "",

                    cardTransactionId:
                        form.paymentMethod ===
                        "CARD"
                            ? referenceNumber
                            : "",

                    chequeNumber:
                        form.paymentMethod ===
                        "CHEQUE"
                            ? referenceNumber
                            : "",

                    notes:
                        String(
                            form.notes ||
                            ""
                        ).trim(),

                    allocations: [
                        {
                            documentId:
                                invoiceId,

                            amount:
                                roundMoney(
                                    form.amount
                                ),

                            allocationType:
                                "INVOICE_PAYMENT",

                            sourceType:
                                "SALE_INVOICE",

                            notes:
                                String(
                                    form.notes ||
                                    ""
                                ).trim(),
                        },
                    ],
                };

                try {
                    setSubmitting(
                        true
                    );

                    setSubmitError(
                        ""
                    );

                    const response =
                        await api.post(
                            config
                                .createBillingPayment,
                            payload
                        );

                    if (
                        !isSuccessfulResponse(
                            response
                        )
                    ) {
                        throw new Error(
                            response
                                ?.message ||
                            "Unable to record payment."
                        );
                    }

                    await swal.success({
                        icon:
                            "success",

                        title:
                            "Payment Recorded",

                        text:
                            response
                                ?.message ||
                            `${formatCurrency(
                                payload.amount
                            )} recorded successfully.`,

                        timer:
                            1500,

                        showConfirmButton:
                            false,
                    });

                    await onSuccess?.(
                        response?.data ||
                        response
                    );
                } catch (
                    requestError
                ) {
                    const message =
                        getResponseError(
                            requestError,
                            "Unable to record sale invoice payment."
                        );

                    setSubmitError(
                        message
                    );

                    swal.error({
                        icon:
                            "error",

                        title:
                            "Payment Failed",

                        text:
                            message,
                    });
                } finally {
                    setSubmitting(
                        false
                    );
                }
            },
            [
                api,
                balanceAmount,
                form,
                invoiceId,
                onSuccess,
                selectedMethod,
                swal,
            ]
        );

    if (
        !open ||
        !resolvedInvoice
    ) {
        return null;
    }

    if (
        balanceAmount <=
        0
    ) {
        return (
            <div
                className="sale-payment-overlay"
                onMouseDown={
                    handleBackdropClick
                }
            >
                <div
                    className="sale-payment-dialog sale-payment-dialog-complete"
                    role="dialog"
                    aria-modal="true"
                    onMouseDown={(
                        event
                    ) =>
                        event.stopPropagation()
                    }
                >
                    <div className="sale-payment-complete-state">
                        <span>
                            <FiCheckCircle />
                        </span>

                        <h3>
                            Invoice Fully Paid
                        </h3>

                        <p>
                            No outstanding balance remains for invoice{" "}
                            <strong>
                                {invoiceNumber}
                            </strong>.
                        </p>

                        <button
                            type="button"
                            onClick={
                                handleClose
                            }
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="sale-payment-overlay"
            onMouseDown={
                handleBackdropClick
            }
        >
            <div
                className="sale-payment-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="sale-payment-dialog-title"
                onMouseDown={(
                    event
                ) =>
                    event.stopPropagation()
                }
            >
                <div className="sale-payment-header">
                    <div className="sale-payment-header-title">
                        <span className="sale-payment-header-icon">
                            <FiCreditCard />
                        </span>

                        <div>
                            <small>
                                SALE INVOICE
                            </small>

                            <h3 id="sale-payment-dialog-title">
                                Record Payment
                            </h3>

                            <p>
                                Allocate a full or partial payment to this invoice.
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="sale-payment-close"
                        onClick={
                            handleClose
                        }
                        disabled={
                            submitting
                        }
                        aria-label="Close payment dialog"
                    >
                        <FiX />
                    </button>
                </div>

                <form
                    onSubmit={
                        handleSubmit
                    }
                >
                    <div className="sale-payment-body">
                        <section className="sale-payment-invoice-card">
                            <div className="sale-payment-invoice-primary">
                                <span className="sale-payment-invoice-avatar">
                                    {String(
                                        customerName ||
                                        "C"
                                    )
                                        .charAt(0)
                                        .toUpperCase()}
                                </span>

                                <div>
                                    <span>
                                        Invoice
                                    </span>

                                    <strong>
                                        {invoiceNumber}
                                    </strong>

                                    <small>
                                        {customerName}
                                    </small>
                                </div>
                            </div>

                            <div className="sale-payment-invoice-status">
                                <span>
                                    Outstanding
                                </span>

                                <strong>
                                    {formatCurrency(
                                        balanceAmount
                                    )}
                                </strong>
                            </div>
                        </section>

                        <section className="sale-payment-summary-grid">
                            <div>
                                <span>
                                    Invoice Total
                                </span>

                                <strong>
                                    {formatCurrency(
                                        grandTotal
                                    )}
                                </strong>
                            </div>

                            <div>
                                <span>
                                    Already Paid
                                </span>

                                <strong className="sale-payment-summary-paid">
                                    {formatCurrency(
                                        alreadyPaid
                                    )}
                                </strong>
                            </div>

                            <div>
                                <span>
                                    Current Balance
                                </span>

                                <strong className="sale-payment-summary-balance">
                                    {formatCurrency(
                                        balanceAmount
                                    )}
                                </strong>
                            </div>
                        </section>

                        {submitError && (
                            <div className="sale-payment-submit-error">
                                <FiAlertCircle />

                                <span>
                                    {submitError}
                                </span>
                            </div>
                        )}

                        <section className="sale-payment-section">
                            <div className="sale-payment-section-heading">
                                <div>
                                    <h4>
                                        Payment Amount
                                    </h4>

                                    <p>
                                        Choose full payment or enter a partial amount.
                                    </p>
                                </div>

                                <span
                                    className={
                                        isFullPayment
                                            ? "sale-payment-type-badge sale-payment-type-full"
                                            : "sale-payment-type-badge sale-payment-type-partial"
                                    }
                                >
                                    {isFullPayment
                                        ? "Full Payment"
                                        : "Partial Payment"}
                                </span>
                            </div>

                            <div className="sale-payment-amount-field">
                                <span>
                                    ₹
                                </span>

                                <input
                                    ref={
                                        amountInputRef
                                    }
                                    type="number"
                                    name="amount"
                                    min="0.01"
                                    max={
                                        balanceAmount
                                    }
                                    step="0.01"
                                    value={
                                        form.amount
                                    }
                                    disabled={
                                        submitting
                                    }
                                    placeholder="0.00"
                                    onChange={
                                        handleChange
                                    }
                                />
                            </div>

                            {errors.amount && (
                                <small className="sale-payment-field-error">
                                    {errors.amount}
                                </small>
                            )}

                            <div className="sale-payment-quick-amounts">
                                <button
                                    type="button"
                                    disabled={
                                        submitting
                                    }
                                    onClick={() =>
                                        setAmount(
                                            balanceAmount
                                        )
                                    }
                                >
                                    Full Balance
                                </button>

                                <button
                                    type="button"
                                    disabled={
                                        submitting
                                    }
                                    onClick={() =>
                                        setAmount(
                                            balanceAmount /
                                                2
                                        )
                                    }
                                >
                                    50%
                                </button>

                                <button
                                    type="button"
                                    disabled={
                                        submitting
                                    }
                                    onClick={() =>
                                        setAmount(
                                            balanceAmount /
                                                4
                                        )
                                    }
                                >
                                    25%
                                </button>
                            </div>

                            <div className="sale-payment-balance-preview">
                                <div>
                                    <span>
                                        Payment Now
                                    </span>

                                    <strong>
                                        {formatCurrency(
                                            enteredAmount
                                        )}
                                    </strong>
                                </div>

                                <div>
                                    <span>
                                        Balance After Payment
                                    </span>

                                    <strong
                                        className={
                                            remainingBalance >
                                            0
                                                ? "sale-payment-balance-remaining"
                                                : "sale-payment-balance-cleared"
                                        }
                                    >
                                        {formatCurrency(
                                            remainingBalance
                                        )}
                                    </strong>
                                </div>
                            </div>
                        </section>

                        <section className="sale-payment-section">
                            <div className="sale-payment-section-heading">
                                <div>
                                    <h4>
                                        Payment Method
                                    </h4>

                                    <p>
                                        Select how the customer paid.
                                    </p>
                                </div>
                            </div>

                            <div className="sale-payment-method-grid">
                                {paymentMethods.map(
                                    (
                                        method
                                    ) => {
                                        const Icon =
                                            method.icon ||
                                            FiCreditCard;

                                        const selected =
                                            form.paymentMethod ===
                                            method.value;

                                        return (
                                            <button
                                                key={
                                                    method.value
                                                }
                                                type="button"
                                                className={
                                                    selected
                                                        ? "sale-payment-method sale-payment-method-selected"
                                                        : "sale-payment-method"
                                                }
                                                disabled={
                                                    submitting
                                                }
                                                onClick={() =>
                                                    handleMethodChange(
                                                        method.value
                                                    )
                                                }
                                            >
                                                <span>
                                                    <Icon />
                                                </span>

                                                <strong>
                                                    {method.label}
                                                </strong>

                                                {selected && (
                                                    <FiCheck className="sale-payment-method-check" />
                                                )}
                                            </button>
                                        );
                                    }
                                )}
                            </div>

                            {errors.paymentMethod && (
                                <small className="sale-payment-field-error">
                                    {errors.paymentMethod}
                                </small>
                            )}
                        </section>

                        <section className="sale-payment-section">
                            <div className="sale-payment-form-grid">
                                <div className="sale-payment-field">
                                    <label>
                                        Payment Date
                                        <span>
                                            *
                                        </span>
                                    </label>

                                    <input
                                        type="date"
                                        name="paymentDate"
                                        value={
                                            form.paymentDate
                                        }
                                        max={
                                            getTodayDate()
                                        }
                                        disabled={
                                            submitting
                                        }
                                        onChange={
                                            handleChange
                                        }
                                    />

                                    {errors.paymentDate && (
                                        <small className="sale-payment-field-error">
                                            {errors.paymentDate}
                                        </small>
                                    )}
                                </div>

                                <div className="sale-payment-field">
                                    <label>
                                        {selectedMethod
                                            ?.referenceLabel ||
                                            "Reference Number"}

                                        <small className="sale-payment-optional-label">
                                            Optional
                                        </small>
                                    </label>

                                    <input
                                        type="text"
                                        name="referenceNumber"
                                        value={
                                            form.referenceNumber
                                        }
                                        disabled={
                                            submitting
                                        }
                                        placeholder={
                                            selectedMethod
                                                ?.referencePlaceholder ||
                                            "Payment reference"
                                        }
                                        onChange={
                                            handleChange
                                        }
                                    />

                                    {errors.referenceNumber && (
                                        <small className="sale-payment-field-error">
                                            {errors.referenceNumber}
                                        </small>
                                    )}
                                </div>

                                <div className="sale-payment-field sale-payment-field-full">
                                    <label>
                                        Notes
                                    </label>

                                    <div className="sale-payment-textarea-wrap">
                                        <FiMessageSquare />

                                        <textarea
                                            name="notes"
                                            rows="3"
                                            maxLength="500"
                                            value={
                                                form.notes
                                            }
                                            disabled={
                                                submitting
                                            }
                                            placeholder="Optional payment note"
                                            onChange={
                                                handleChange
                                            }
                                        />
                                    </div>

                                    <div className="sale-payment-notes-meta">
                                        {errors.notes ? (
                                            <small className="sale-payment-field-error">
                                                {errors.notes}
                                            </small>
                                        ) : (
                                            <small>
                                                Optional
                                            </small>
                                        )}

                                        <small>
                                            {String(
                                                form.notes ||
                                                ""
                                            ).length}
                                            /500
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <div className="sale-payment-information">
                            <FiInfo />

                            <p>
                                This payment will be allocated to invoice{" "}
                                <strong>
                                    {invoiceNumber}
                                </strong>{" "}
                                and its payment status and outstanding balance will be recalculated automatically.
                            </p>
                        </div>
                    </div>

                    <div className="sale-payment-footer">
                        <button
                            type="button"
                            className="sale-payment-cancel-button"
                            disabled={
                                submitting
                            }
                            onClick={
                                handleClose
                            }
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            className="sale-payment-submit-button"
                            disabled={
                                submitting ||
                                enteredAmount <=
                                    0 ||
                                enteredAmount >
                                    balanceAmount
                            }
                        >
                            {submitting ? (
                                <>
                                    <FiLoader className="sale-payment-submit-spinner" />

                                    Recording Payment...
                                </>
                            ) : (
                                <>
                                    <FiCheckCircle />

                                    Record{" "}
                                    {formatCurrency(
                                        enteredAmount
                                    )}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export {
    PAYMENT_METHODS,
    EMPTY_FORM,
    formatCurrency,
    getInvoiceId,
    getInvoiceNumber,
    getCustomerName,
    getGrandTotal,
    getPaidAmount,
    getBalanceAmount,
    validatePayment,
};

export default SaleInvoicePaymentDialog;